import { useEffect, useState, useMemo } from "react";
import { Search, Package, ChevronDown, Hash, Warehouse } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function MobileMateriali() {
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const wid = user?.magazzino_warehouse_id;

  useEffect(() => {
    if (!wid) { setInv([]); return; }
    api.get("/magazzino/inventory", { params: { warehouse_id: wid } })
      .then((r) => setInv(r.data)).catch(() => setInv([]));
  }, [wid]);

  const products = useMemo(() => {
    const map = {};
    (inv || []).forEach((r) => {
      const k = r.product_id;
      if (!map[k]) map[k] = { product_id: k, sku: r.sku, name: r.name, manufacturer_code: r.manufacturer_code, supplier_name: r.supplier_name, is_serialized: r.is_serialized, unit: r.unit_of_measure || "pz", serials: [], qty: 0 };
      map[k].qty += r.stock || 0;
      if (r.serial_number) map[k].serials.push(r.serial_number);
    });
    let list = Object.values(map).filter((p) => p.qty > 0 || p.serials.length);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || (p.manufacturer_code || "").toLowerCase().includes(s) || (p.supplier_name || "").toLowerCase().includes(s));
    }
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [inv, q]);

  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-navy">Materiali</h1>
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Warehouse size={14} />{user?.magazzino_warehouse_name || "Magazzino mobile"}
        </p>
      </div>

      {!wid ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm font-semibold text-amber-800" data-testid="materiali-nowarehouse">
          Nessun magazzino mobile collegato al tuo account. Contatta l'amministratore.
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca prodotto o SKU..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="materiali-search" />
          </div>

          <div className="space-y-2.5">
            {inv === null ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)
            ) : products.length ? (
              products.map((p) => (
                <div key={p.product_id} className="rounded-xl border border-border bg-card" data-testid={`materiali-product-${p.sku}`}>
                  <button
                    onClick={() => setOpen(open === p.product_id ? null : p.product_id)}
                    className="flex w-full items-center gap-3 p-3.5 text-left"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Package size={18} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-navy break-words">{p.name}</p>
                      <p className="font-mono text-xs text-muted-foreground break-words">Cod. {p.manufacturer_code || p.sku || "—"}{p.supplier_name ? ` · ${p.supplier_name}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-bold text-navy">{p.qty} {p.unit}</span>
                      {p.is_serialized && <ChevronDown size={16} className={cn("text-muted-foreground transition-transform", open === p.product_id && "rotate-180")} />}
                    </div>
                  </button>
                  {open === p.product_id && p.is_serialized && (
                    <div className="border-t border-border px-3.5 py-3">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Seriali in giacenza ({p.serials.length})</p>
                      {p.serials.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {p.serials.map((s) => (
                            <span key={s} className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/40 px-2 py-1 font-mono text-xs text-navy">
                              <Hash size={10} className="text-muted-foreground" />{s}
                            </span>
                          ))}
                        </div>
                      ) : <p className="text-xs text-muted-foreground">Nessun seriale</p>}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">Nessun prodotto in giacenza</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
